import { type api, useInfoQuery } from "@/services/api";
import { Info } from "@/services/apiTypes";
import type { QueryHookResult } from "@Light/services/queryTypes";
import { Provider as LightProvider } from "@Light/scaffold/Provider";
import { createContext, useContext } from "react";
import { scaffold as defaultScaffold } from "../scaffold";
import { HeaderLogo } from "./HeaderLogo";

export type InfoState = QueryHookResult<
  typeof api.endpoints.info.Types.QueryDefinition
>;

export const InfoContext = createContext<InfoState | undefined>(undefined);

export type WithInfoProps = {
  children: React.ReactNode;
};

export const WithInfo: React.FC<WithInfoProps> = ({
  children,
}: WithInfoProps) => {
  const info = useInfoQuery();

  if (info.isLoading) {
    return null;
  }

  children = (
    <InfoContext.Provider value={info}>{children}</InfoContext.Provider>
  );
  if (!info.data) {
    return children;
  }

  const configuration = info.data.configuration;

  const scaffold = {
    ...defaultScaffold,
    page: {
      ...defaultScaffold.page,
      companyName: configuration.company_name,
      brandMode: configuration.brand_mode,
      legalEntityName: configuration.legal_entity_name,
      termsOfServiceUrl: configuration.terms_of_service_url ?? undefined,
      supportEmail: configuration.support_email,
      supportPhone: configuration.support_phone,
      supportHours: configuration.support_hours ?? "",
      header: {
        ...defaultScaffold.page.header,
        headerLogo: configuration.logo ? HeaderLogo : undefined,
      },
    },
  };
  return <LightProvider scaffold={scaffold}>{children}</LightProvider>;
};

export function useInfo(): Info | undefined {
  const info = useContext(InfoContext);
  if (!info?.isSuccess || info.error || !info.data) {
    return undefined;
  }

  return info.data;
}

export function useRequiredInfo() {
  const info = useInfo();
  if (!info) {
    throw new Error("info required for use with useRequiredInfo");
  }

  return info;
}
